/* eslint-disable no-unused-vars,@typescript-eslint/no-unused-vars,@typescript-eslint/dot-notation,dot-notation */
import React, { ComponentClass, FunctionComponent, ForwardRefExoticComponent } from 'react';
import { disposeOnUnmount } from 'mobx-react';
import hoistStatics from 'hoist-non-react-statics';
import BasePresenter from './base-presenter';
import { IViewDelegate } from './interface/IViewDelegate';
import PropTypes from 'prop-types';
import { dispatchFnMap } from './decorate/dispatchProps';
import { externalFnMap } from './decorate/external';
import { isLoadPresenter } from './config';
import { IModel } from './interface/IModel';
import classMap from './class-map';
import { presenterType, modelType, Disposer } from './type';
import EmptyModel from './empty-model';
import { parseTargetKey } from '@src/js/separate-logic/utils';

let pid = 100;  // 绑定presenter的id值
type WrapperProps = {
    forwardedRef: any
};

type WrapperState = {

};

/**
 * 使用connect函数，链接mvp。
 * @param Presenter 从组件中拆分的logic，可以传递一个BasePresenter类，或者() => import('./xx')函数
 * @param Model 从组件中拆分的数据，主要是render中需要的数据
 * @param WrappedComponent 被拆分的组件，主要包含render函数和UI显示数据相关的计算函数
 */
function connect<A extends BasePresenter, B extends IModel>(
    Presenter: presenterType<A> | (() => Promise<any>),
    Model: modelType<B> | null,
    WrappedComponent: ComponentClass<any, any> | FunctionComponent<any>,
): ForwardRefExoticComponent<any> {
    const { ForwardComponent } = connectInner(Presenter, Model, WrappedComponent);
    return ForwardComponent;
}

/**
 * connectInner包装了一层。
 * 方便写单测
 */
function connectInner<A extends BasePresenter, B extends IModel>(
    Presenter: presenterType<A> | (() => Promise<any>),
    Model: modelType<B> | null,
    WrappedComponent: ComponentClass<any, any> | FunctionComponent<any>,
): { ForwardComponent: ForwardRefExoticComponent<any>, ComponentWrapper: ComponentClass } {
    const TAG = WrappedComponent.displayName || WrappedComponent.name;
    const isClass = isPresenterClass(Presenter);
    if (isClass) {
        mountComponentExternal(Presenter, WrappedComponent);
    }
    // eslint-disable-next-line no-plusplus,no-param-reassign
    WrappedComponent['pid'] = pid++;
    hookWrappedComponent(WrappedComponent);
    class ComponentWrapper extends React.Component<WrapperProps, WrapperState> implements IViewDelegate {
        public static contextTypes = {
            store: PropTypes.object,
        };
        private myPresenter: BasePresenter;
        private refMap = new Map<string, any>();
        private dispatchFunMap = {};
        private useTempPresenter = false; // 是否使用的是临时的presenter
        private willUnMount = false;
        private isAutoImportFailed;  // 处理autoImport失败的情况

        public constructor(props, context) {
            super(props, context);
            if (!isPresenterClass(Presenter)) {  // 普通函数,返回一个promise
                const savedPresenterClass = classMap.getClass(WrappedComponent['pid']);
                if (!savedPresenterClass) {
                    this.autoImport();
                }
                let presenterClass;
                const loadPresenter = isLoadPresenter();  // 是否去加载presenter
                // window.log && window.log.info(`${TAG} constructor loadPresenter=`, loadPresenter);
                if (loadPresenter && savedPresenterClass) {
                    presenterClass = savedPresenterClass;
                } else {
                    this.useTempPresenter = true;
                    presenterClass = BasePresenter;
                }
                this.createPresenter(presenterClass);
            } else {  // presenter类函数
                this.createPresenter(Presenter as presenterType<A>);
            }
        }

        /* -----------------View delegate start---------------------------- */

        public getChildRef(name: string) {
            return this.refMap.get(name);
        }
        public getProps(): object {
            return this.props;
        }
        public getStore() {
            return this.context.store;
        }
        public setUpdate(callBack?: () => void): void {
            this.setState({}, callBack);
        }

        public disposeOnUnmount<TF extends Disposer | Disposer[]>(fn: TF): TF {
            return disposeOnUnmount(this, fn);
        }

        /* -----------------View delegate end---------------------------- */

        /* -----------------生命周期函数 start---------------------------- */
        public componentDidMount() {
            this.myPresenter?.componentDidMount();
        }

        public componentWillUnmount() {
            this.myPresenter?.componentWillUnmount();
            this.willUnMount = true;
            this.refMap.clear();
        }

        // eslint-disable-next-line camelcase
        public UNSAFE_componentWillReceiveProps(nextProps) {
            if (this.shouldLoadPresenter()) {  // 父组件刷新，触发子组件刷新，判断是否要加载presenter
                const savedPresenterClass = classMap.getClass(WrappedComponent['pid']);
                this.useTempPresenter = false;
                this.createPresenter(savedPresenterClass as presenterType<A>);
            }
            // 处理动态Import失败的情况
            if (this.isAutoImportFailed && this.useTempPresenter) {
                this.autoImport();
            }
            this.myPresenter?.componentWillReceiveProps(nextProps);
        }

        public shouldComponentUpdate(nextProps): boolean {
            if (this.shouldLoadPresenter()) {  // 需要加载presenter，则强制刷新
                return true;
            }
            const presenter = this.myPresenter;
            if (presenter) {
                const nextModel = presenter.getNextModel();
                const shouldUpdate = presenter.shouldComponentUpdate(nextProps, nextModel);
                if (!shouldUpdate) {  // shouldUpdate返回false，不会执行willUpdate去设置nextModel
                    presenter.updateModelWithNextModel();
                }
                return shouldUpdate;
            }
            return true;
        }

        public componentDidUpdate(prevProps): void {
            const presenter = this.myPresenter;
            if (presenter) {
                const preModel = presenter.getPreModel();
                presenter.componentDidUpdate(prevProps, preModel);
                presenter.clearPreModel();
            }
        }

        // eslint-disable-next-line camelcase
        public UNSAFE_componentWillUpdate(nextProps): void {
            const presenter = this.myPresenter;
            if (presenter) {
                const nextModel = presenter.getNextModel();
                presenter.componentWillUpdate(nextProps, nextModel);
                presenter.updateModelWithNextModel();
            }
        }
        /* -----------------生命周期函数 end---------------------------- */

        public render() {
            const { forwardedRef } = this.props;
            // window.log && window.log.info('render this.myPresenter=', classMap.size());
            const modelProps = this.myPresenter?.getModel();
            return <WrappedComponent ref={forwardedRef} { ...this.props} { ...this.dispatchFunMap }
                { ...modelProps } setRef={this.setRef} __dispatchExternalFn={this.dispatchLogicFn}/>;
        }

        /**
         * 判断可否加载Presenter
         */
        private shouldLoadPresenter(): boolean {
            const savedPresenterClass = classMap.getClass(WrappedComponent['pid']);
            const loadPresenter = isLoadPresenter();  // 是否去加载presenter
            return !!savedPresenterClass && loadPresenter && this.useTempPresenter;
        }

        /**
         * 动态加载Presenter
         */
        private autoImport() {
            this.isAutoImportFailed = false;
            const loadPresenter = isLoadPresenter();  // 记录一下，import前是否去加载presenter
            (Presenter as () => Promise<any>)().then((modal) => {
                const presenterClass = modal.default as presenterType<A>;
                classMap.setClass(WrappedComponent['pid'], presenterClass);
                // 加载完presenter后，设置组件的external接口
                mountComponentExternal(presenterClass, WrappedComponent);

                if (this.willUnMount) return;  // 组件将被移除，直接返回

                if (loadPresenter && this.useTempPresenter) {
                    this.useTempPresenter = false;
                    // window.log && window.log.info(`${TAG} autoImport完毕，需要load presenter`);
                    this.createPresenter(presenterClass);
                    this.forceUpdate();  // 更新组件
                }
            })
                .catch((error) => {
                    this.isAutoImportFailed = true;
                    window.log?.error(`${TAG} auto import error`, error);
                });
        }

        /**
         * 保存子组件的ref引用
         * @param name
         * @param ref
         */
        private setRef = (name, ref)  => {
            this.refMap.set(name, ref);
        };

        /**
         * props传递给被包裹组件的函数以及组件对外暴露的接口，都会使用此方法进行转发
         * @param fnName 函数名称
         * @param args 参数列表
         */
        private dispatchLogicFn = (fnName, ...args) => {
            const { isSet, isGet, name } = parseTargetKey(fnName);
            if (isGet) {
                return this.myPresenter[name];
            }
            if (isSet) {
                // eslint-disable-next-line prefer-destructuring
                this.myPresenter[name] = args?.[0];
                return;
            }
            return this.myPresenter[name](...args);
        };

        /**
         * 创建presenter
         * @param presenterClass 可能是临时的presenter
         */
        private createPresenter(presenterClass: presenterType<A>) {
            const Presenter = presenterClass;
            this.myPresenter = new Presenter(Model ? new Model() : new EmptyModel(), this);
            // 初始化逻辑挪到这里，不放入BasePresenter的construct中，否则会导致取不到属性值
            this.myPresenter.init(this.getProps());

            if (this.useTempPresenter) {  // 如果使用的是临时的presenter，则清空往下传递的函数列表
                this.dispatchFunMap = {};
                // window.log && window.log.info(`${TAG} createTempPresenter`);
            } else {
                const dispatchFnArray = getAllDecoratorFn(dispatchFnMap, Presenter);
                // window.log && window.log.info(`${TAG} createRealPresenter dispatchFnArray=`, dispatchFnArray);
                dispatchFnArray?.forEach((fnName) => {
                    this.dispatchFunMap[fnName] = (...args) => this.dispatchLogicFn(fnName, ...args);
                });
            }
        }
    }

    const forwarded = React.forwardRef((props, ref) => <ComponentWrapper {...props} forwardedRef={ref}/>);
    forwarded.displayName = TAG;
    return { ForwardComponent: hoistStatics(forwarded, WrappedComponent), ComponentWrapper };
}

// 禁止类组件使用的函数
const disableFun = ['setState'];
/**
 * 对组件本身的刷新行为进行禁止。
 * 注意：对函数式组件无效。因为函数组件本身采用了hook方式
 * @param component
 */
function hookWrappedComponent(component) {
    const wrappedComponent = component;
    disableFun.forEach((method) => {
        if (wrappedComponent.prototype[method]) {
            wrappedComponent.prototype[method] = function () {
                throw new Error(`stop use component method ${method}, use Presenter.setModel()`);
            };
        }
    });
}

/**
 * 挂载组件的external方法
 * @param Presenter 类Presenter
 * @param WrappedComponent 类组件
 */
function mountComponentExternal(Presenter, WrappedComponent) {
    const externalFnArray = getAllDecoratorFn(externalFnMap, Presenter);
    // window.log && window.log.info(`${WrappedComponent.name} externalFnArray=`, externalFnArray);
    externalFnArray?.forEach((fnName) => {
        const { isSet, isGet, name } = parseTargetKey(fnName);
        // eslint-disable-next-line no-prototype-builtins
        if (!WrappedComponent.prototype.hasOwnProperty(name)) {
            const dispatchFn = function (...args) {
                // this为 WrappedComponent
                // eslint-disable-next-line no-underscore-dangle
                return this.props.__dispatchExternalFn(fnName, ...args);
            };
            if (isGet || isSet) {
                const des: any = { configurable: true, enumerable: false };
                isGet && (des.get = dispatchFn);
                isSet && (des.set = dispatchFn);
                Object.defineProperty(WrappedComponent.prototype, name, des);
            } else {
                // eslint-disable-next-line no-param-reassign
                WrappedComponent.prototype[name] = dispatchFn;
            }
        }
    });
}

/**
 * 获取Presenter类继承关系上所有保存在fnMap的方法
 * @param fnMap
 * @param Presenter
 */
function getAllDecoratorFn(fnMap: Map<any, string[]>, Presenter): string[] {
    let subClass = Presenter;
    const fnArray: string[] = [];
    while (subClass && subClass !== BasePresenter) {
        const array = fnMap.get(subClass.prototype);
        if (array) {
            fnArray.push(...array);
        }
        subClass = subClass.__proto__; // 通过__proto__获取父类
    }
    return fnArray;
}

/**
 * Presenter是否是一个类
 * @param Presenter
 */
function isPresenterClass(Presenter) {
    return typeof Presenter['presenterID'] !== 'undefined';
}

export default connect;

export const testTable = {
    hookWrappedComponent,
    connectInner,
    getAllDecoratorFn,
};
